'use client';

import React from 'react';
import { Navbar, Footer } from '../../components/Layout';
import Image from 'next/image';
import { Leaf, Award, Heart, TrendingUp, ChevronRight, Users, Eye, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const MILESTONES = [
  {
    year: '2018',
    title: 'Seeds of an Idea',
    desc: 'Began in Erode, Tamil Nadu, with a small group of 5 farmers wanting to restore health back to local soil.',
    icon: '🌱'
  },
  {
    year: '2020',
    title: 'Cultivating Connections',
    desc: 'Formed our first cooperative partnership, bringing 50+ local organic farms online.',
    icon: '🤝'
  },
  {
    year: '2023',
    title: 'Empowering the Soil',
    desc: 'Launched traditional wood-pressing (Marachekku) units and certified testing processes for zero chemicals.',
    icon: '🚜'
  },
  {
    year: '2026',
    title: 'Nurturing 10,000+ Homes',
    desc: 'Connecting thousands of conscious consumers directly with chemical-free agricultural delicacies.',
    icon: '🏡'
  }
];

export default function OurStory() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFB] font-body text-text-primary overflow-x-hidden selection:bg-amber/30 selection:text-deep-green">
      <Navbar />

      <main className="pb-28">
        {/* Dynamic Parallax Hero */}
        <section className="relative h-[65vh] flex items-center justify-center overflow-hidden">
          <Image
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2064&auto=format&fit=crop"
            alt="Organic Farm"
            fill
            className="object-cover scale-105 animate-[pulse_10s_infinite]"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#FDFDFB]" />
          
          <div className="relative z-10 text-center max-w-4xl px-6">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-1.5 bg-amber/20 border border-amber/30 px-4 py-1.5 rounded-full text-amber text-xs font-black uppercase tracking-widest mb-6"
            >
              <Sparkles className="w-3.5 h-3.5" /> Nurturing Our Fields
            </motion.div>
            
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-8xl font-display font-black text-white mb-6 leading-tight drop-shadow-md"
            >
              Our Roots
            </motion.h1>
            
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg md:text-2xl text-white/90 font-medium leading-relaxed max-w-2xl mx-auto drop-shadow"
            >
              A bridge connecting ethical Tamil Nadu farmers directly to your family dining table.
            </motion.p>
          </div>
        </section>

        {/* The Origin Narrative */}
        <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 relative">
          <div className="absolute top-20 right-10 w-96 h-96 bg-pale-green/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-amber/5 rounded-full blur-3xl -z-10" />

          <div className="grid md:grid-cols-12 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="md:col-span-7 text-left space-y-6"
            >
              <div className="inline-flex items-center gap-2">
                <div className="w-12 h-[2px] bg-amber rounded-full"></div>
                <span className="text-xs font-black text-amber uppercase tracking-widest">The Beginning</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-display font-black text-deep-green mb-6 leading-tight">
                The Origin of <span className="text-transparent bg-clip-text bg-gradient-to-r from-mid-green to-deep-green">Pasumai Virumbi</span>
              </h2>
              
              <p className="text-base sm:text-lg text-text-muted leading-relaxed font-medium">
                It all started with a simple realization: the food we consume today is far from what nature intended. Filled with preservatives, pesticides, and artificial enhancements, our modern diet was taking a toll on our health and our environment.
              </p>
              
              <p className="text-base sm:text-lg text-text-muted leading-relaxed font-medium">
                In 2018, a group of passionate farmers and health enthusiasts in Erode, Tamil Nadu, came together with a shared vision. We wanted to revive traditional farming methods, protect our soil, and provide our families with food that truly nourishes the body and soul.
              </p>
              
              <div className="p-6 bg-gradient-to-br from-pale-green/30 to-surface rounded-3xl border border-mid-green/10 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber/5 rounded-full blur-xl"></div>
                <p className="text-sm sm:text-base leading-relaxed font-black text-deep-green">
                  🌱 Thus, Pasumai Virumbi was born—a digital bridge connecting ethical farmers directly to conscious consumers like you.
                </p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="md:col-span-5 relative h-[500px] md:h-[600px] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white"
            >
              <Image 
                src="https://images.unsplash.com/photo-1595804595240-3b02ce38b816?q=80&w=1964&auto=format&fit=crop" 
                alt="Farmers working" 
                fill 
                className="object-cover hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-6 rounded-2xl border border-white/50 text-left shadow-lg">
                <span className="text-xs font-black text-mid-green uppercase tracking-widest block mb-1">Empowering Rural Tamil Nadu</span>
                <p className="text-[11px] font-bold text-text-muted leading-relaxed">We pay up to 35% higher wages directly to farmers to ensure sustainable livelihoods.</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Milestone Timeline */}
        <section className="py-24 bg-gradient-to-b from-[#FDFDFB] via-surface to-[#FDFDFB]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-amber rounded-full"></div>
              <span className="text-xs font-black text-amber uppercase tracking-widest">Our Journey</span>
              <div className="w-8 h-[2px] bg-amber rounded-full"></div>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-display font-black text-deep-green mb-16">Milestones of Purity</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
              {/* Timeline Connector Line */}
              <div className="absolute top-12 left-0 right-0 h-[2px] bg-gradient-to-r from-amber via-mid-green to-amber hidden md:block -z-10" />
              
              {MILESTONES.map((stone, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                  className="bg-white p-8 rounded-[2rem] border border-border/80 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 relative text-left group"
                >
                  <div className="w-14 h-14 bg-pale-green rounded-2xl flex items-center justify-center text-2xl mb-6 shadow-inner border border-pale-green/20 group-hover:scale-110 transition-transform">
                    {stone.icon}
                  </div>
                  <span className="text-amber font-black text-xl md:text-2xl block mb-2 font-display">{stone.year}</span>
                  <h3 className="text-lg font-black text-deep-green mb-3">{stone.title}</h3>
                  <p className="text-text-muted text-xs leading-relaxed font-semibold">{stone.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-[2px] bg-amber rounded-full"></div>
              <span className="text-xs font-black text-amber uppercase tracking-widest">What We Stand For</span>
              <div className="w-8 h-[2px] bg-amber rounded-full"></div>
            </div>
            <h2 className="text-3xl md:text-5xl font-display font-black text-deep-green mb-4">Our Core Values</h2>
            <p className="text-text-muted text-sm md:text-base font-semibold max-w-xl mx-auto">These principles guide every decision we make, from the seeds we plant to the packaging we use.</p>
          </div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8"
          >
            {[
              { icon: <Leaf className="w-8 h-8 text-mid-green" />, title: "100% Organic", desc: "No chemical additives, no compromises. We only source from certified organic fields." },
              { icon: <Heart className="w-8 h-8 text-amber" />, title: "Ethical Sourcing", desc: "Fair trade rates that support local farming families and reward sustainable practices." },
              { icon: <TrendingUp className="w-8 h-8 text-mid-green" />, title: "Eco-Conscious", desc: "From plastic-free glass jar packaging to water-smart drip irrigation methods." },
              { icon: <Award className="w-8 h-8 text-amber" />, title: "Unmatched Purity", desc: "Wood-pressed oils, raw forest honey, stone-ground flours. Truly raw and healthy." }
            ].map((v, i) => (
              <motion.div 
                key={i} 
                variants={itemVariants}
                className="bg-white p-8 rounded-[2rem] shadow-sm border border-border/60 text-center hover:shadow-lg transition-all group cursor-pointer hover:border-mid-green/30"
              >
                <div className="w-20 h-20 bg-pale-green/40 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-pale-green transition-colors border border-pale-green/20">
                  {v.icon}
                </div>
                <h3 className="text-lg font-black text-deep-green mb-3">{v.title}</h3>
                <p className="text-text-muted text-xs leading-relaxed font-semibold">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
